import { Component, Input, OnChanges } from '@angular/core';
import { Product } from '../../../model/product/product.model';
import { lineColorSet, lineTabFill } from '../line-color-set';
import { lineTabOff, lineTabOn } from '../line-tab-colors';
import { ORB_BANDS, ORB_COLORS, ORB_R, ORB_RIM_STOPS, ORB_STARS, ORB_TAB_SET } from '../line-orb-glyph';
import { TREE_BARK, TREE_CANOPY, TREE_DARK, TREE_LIGHT, TREE_MID, TREE_TRUNK } from '../line-tree-glyph';
import { MAPLE_BLADE, MAPLE_DEEP, MAPLE_LIGHT, MAPLE_MID } from '../line-maple-glyph';
import { SUN_CY, SUN_HORIZON, SUN_LIGHT, SUN_R, SUN_RAY_LEN, SUN_TAB_HORIZON_D, SUN_TAB_TRANSFORM, sunRays } from '../line-sun-glyph';
import { MIAMI_PALM_CROWN, MIAMI_PALM_TRUNK } from '../line-miami-palm-glyph';

/**
 * A LINE "Home" (friends) screen preview rendered in the iPhone frame, themed
 * from `designTokens`. Its whole point is to show the theme's assets in real
 * context, especially the MENU BACKGROUND, which sits live behind the bottom
 * tab bar (Home / Chats / Calls). Dark-LINE layout: status bar, top actions,
 * profile, search, Services row, a friends hint, and the menu-bg tab bar.
 */
@Component( {
  selector: 'app-line-home',
  templateUrl: './line-home.component.html',
  styleUrls: [ './line-home.component.scss' ],
} )
export class LineHomeComponent implements OnChanges {

  @Input() product!: Product;

  /** The theme's product line (e.g. "Nature" / "Cities"), used as the group. */
  @Input() category = '';

  /**
   * iOS 26 (Liquid Glass) bottom bar: a floating translucent pill with the tabs
   * and an active "pill" behind Home, instead of the classic tab bar sitting on
   * the menu-background strip (owner 2026-07-29). Mirrors how LINE renders the
   * theme on iOS 26+.
   */
  @Input() ios26 = false;

  readonly frameSrc = './assets/common/iphone-17-frame.webp';

  name = '';
  logo = '';
  status = '';
  /** Friend row name. */
  readonly friendName = 'Alice';
  editorBg = '#142B37';
  surface = '#1A3746';
  accent = '#549EFF';
  fg = '#ECF0F4';
  fgDim = '#85A2B3';
  /** Text colour on the coloured header block (white on the pink Sakura header). */
  headerText = '#ECF0F4';
  /** Header block background: the theme's header colour when it has a distinct
   *  coloured header (Sakura pink), else transparent so other themes are
   *  unchanged (the body background shows through). */
  headerBg = 'transparent';
  /** Search-bar background: white on themes with a coloured header (Sakura),
   *  else empty so the default translucent fill applies. */
  searchBg = '';
  /** iOS 26 Liquid Glass bar colours — WHITE glass on light themes, DARK glass
   *  on dark themes (owner 2026-07-29). */
  glassBg = '#FFFFFF';
  glassPill = '#F2EBEF';
  glassOnText = '#343434';
  /** iOS 26 TOP actions pill (bookmark / bell / + / gear) and the ink in it.
   *  Until 2026-08-13 this pill was a hardcoded `rgba(255,255,255,0.26)` wash in
   *  the SCSS with no per-theme hook at all, so every theme got the same white
   *  glass no matter what its skin actually draws — the owner spotted it on
   *  Roraima, whose skin paints a warm brown capsule. Empty here means "keep the
   *  old wash", so no other theme moves until its own screenshot is sampled. */
  topPillBg = '';
  topPillInk = '';
  /** Classic tab bar: the fill colour BELOW the menu-bg strip (the home-indicator
   *  area / color skin), matching the phone (owner 2026-07-29). */
  tabFill = 'transparent';

  /** Aurora's Home tab draws the shared orb instead of a Phosphor mask. */
  readonly orbR = ORB_R;
  readonly orbRimStops = ORB_RIM_STOPS;
  readonly orbStars = ORB_STARS[ 0 ];
  readonly orbFit = 'translate(60,60) scale(1.228) translate(-60,-60)';
  /** Sequoia's Home tab draws the shared redwood. */
  readonly treeCanopy = TREE_CANOPY;
  readonly treeTrunk = TREE_TRUNK;
  readonly treeDark = TREE_DARK;
  readonly treeMid = TREE_MID;
  readonly treeLight = TREE_LIGHT;
  readonly treeBark = TREE_BARK;
  /** Autumn's Home tab draws the shared maple. */
  readonly mapleBlade = MAPLE_BLADE;
  readonly mapleDeep = MAPLE_DEEP;
  readonly mapleMid = MAPLE_MID;
  readonly mapleLight = MAPLE_LIGHT;
  /** Roraima's Home tab draws the shared SETTING SUN — exactly what
   *  line-menu-button exports into i_30, so the preview cannot promise a glyph
   *  the phone will not draw. */
  readonly sunR = SUN_R;
  readonly sunCy = SUN_CY;
  readonly sunHorizonY = SUN_HORIZON;
  readonly sunHorizonD = SUN_TAB_HORIZON_D;
  readonly sunShift = SUN_TAB_TRANSFORM;
  readonly sunRaysD = sunRays( SUN_CY, SUN_RAY_LEN );
  readonly sunLight = SUN_LIGHT;
  /** Miami's Home tab draws the shared palm — exactly what line-menu-button
   *  exports into i_30. */
  readonly palmCrown = MIAMI_PALM_CROWN;
  readonly palmTrunk = MIAMI_PALM_TRUNK;
  readonly orbBands = ORB_TAB_SET.map( b => ( {
    d: ORB_BANDS[ b.band ], fill: ORB_COLORS[ b.color ], o: b.o,
  } ) );

  /** Active (Home) tab colour, the electric green shared with the menu
   *  background ribbons and the tab ON glyphs. */
  /** The five slots the mockup shows, in LINE's default order. Each one renders
   *  a real app-line-menu-button, so this list is the ONLY thing the preview
   *  knows about tabs — the glyphs, colours and sizes all come from the asset
   *  component. Home is the active one. */
  readonly previewTabs: { name: string, label: string, on?: boolean }[] = [
    { name: 'home', label: 'Home', on: true },
    { name: 'chats', label: 'Chat' },
    { name: 'voom', label: 'Voom' },
    { name: 'calls', label: 'Calls' },
    { name: 'wallet', label: 'Wallet' },
  ];

  tabOn = '#3DFFB0';
  /** OFF glyph colour for the preview tab bar (mirrors line-menu-button). */
  tabOff = '';

  ngOnChanges(): void {
    const p = this.product;
    if ( !p ) {
      return;
    }
    this.name = p.name || '';
    this.logo = p.logo || '';
    this.status = p.tags?.length ? p.tags.join( ' · ' ) : 'made by Codigrate';
    // Base colours come from the chosen predefined set; only the group badge
    // takes the theme's own key colour.
    const set = lineColorSet( p.key, p.appearance );
    this.editorBg = set.bg;
    this.surface = set.surface;
    this.fg = set.fg;
    this.fgDim = set.fgDim;
    this.headerText = set.headerText || set.fg;
    this.headerBg = set.headerText ? set.surface : 'transparent';
    // A white search field only reads on a COLOURED header; Everest's skin makes
    // the header white too, so its field takes LINE's grey (sampled).
    this.searchBg = p.key === 'everest' ? '#EEEEEE'
      : p.key === 'aurora-borealis' ? '#1D1D1D'
        : p.key === 'sequoia' ? '#EFEFDB'
          : p.key === 'autumn' ? '#F9EDDB'               // sampled: pale sand field
            : p.key === 'roraima' ? '#644434'            // sampled: a step up from the body
              : p.key === 'miami' ? '#FFF2F6'            // sampled: the palest pink
                : p.key === 'istanbul' ? '#DBF4FA'       // sampled: pale cyan, not white
                  // PLACEHOLDER: Roraima's search field, verbatim — the owner
                  // has not picked Paris's LINE skin yet and will set it
                  // themselves. Resample from device screenshots then.
                  : p.key === 'paris' ? '#FFE6E6'       // sampled: the pale pink search field
                    : p.key === 'rio-de-janeiro' ? '#F5F5F5'  // sampled: the grey field
                      : p.key === 'tallinn' ? '#F3F3F3'  // sampled: a neutral grey field
                        // Ocean: SAMPLED. Not the white field the light skins
                        // usually take and not the dark one this theme was built
                        // for either — a pale sea-mist tinted toward the header,
                        // sitting in the teal band the way Istanbul's does.
                        : p.key === 'ocean' ? '#D7F2EF'
                          // Salda: SAMPLED, and not the white field this was
                          // derived as — a pale PEACH one, carrying the same warm
                          // cast the skin's input bar makes explicit.
                          : p.key === 'salda' ? '#FAE9DA'
                            // Prague: SAMPLED. Not a step up from a navy body —
                            // this skin is LINE's near-black, so the field is the
                            // same near-black step Aurora's takes.
                            : p.key === 'prague' ? '#1D1D1D'
                              // Sydney: SAMPLED — LINE's neutral GREY field, not
                              // the white this was derived as. This skin keeps its
                              // blue for the header and the keypad and leaves the
                              // rest of the chrome grey.
                              : p.key === 'sydney' ? '#F3F3F3'
                                // Reynisfjara: a step up from the dark body, the
                                // shape every dark skin here takes.
                                : p.key === 'reynisfjara' ? '#1D1D1D'
                                  // Spring: the white field every light skin with
                                  // a coloured header takes.
                                  : p.key === 'spring' ? '#E5F3D1'
                                    // Vienna: a step up from the wine body, the
                                    // shape every dark skin here takes.
                                    : p.key === 'vienna' ? '#F7DFD8'
                                      // Madrid: the white field every light skin
                                      // with a coloured header takes.
                                      : p.key === 'madrid' ? '#FFF8DD'
                                        // Etna: a step up from the black body.
                                        : p.key === 'etna' ? '#1D1D1D'
                                          // Fuji: the white field every light skin
                                          // with a coloured header takes.
                                          : p.key === 'fuji' ? '#FFFAF9'
          : ( set.headerText ? '#FFFFFF' : '' );
    // iOS 26 glass is LIGHT for every theme — the same look Sakura already
    // shows — NOT a dark bar on dark themes (owner 2026-08-07, from the device;
    // the earlier `rgba(24,26,32,0.72)` dark-mode branch was simply wrong).
    //
    // This matters beyond looks: the old preview drew a dark bar with an almost
    // invisible active pill (`rgba(255,255,255,0.16)`, compositing to ~#3D3F44),
    // so every light ON glyph read beautifully here and then vanished on the
    // phone. That flattering mockup is what let the Tokyo problem through review.
    //
    // One light set for all themes: white bar, pale active pill, dark ink on it.
    this.glassBg = '#FFFFFF';
    this.glassPill = '#F2EBEF';
    this.glassOnText = '#343434';
    // ...but the glass takes its cue from the COLOUR SKIN, not from iOS: on
    // Aurora's near-black skin the owner's device shows a dark bar and a dark
    // active pill (screenshot 2026-08-12), so the one-light-set rule above only
    // holds for the light skins it was sampled from.
    if ( p.key === 'aurora-borealis' ) {
      this.glassBg = '#1C1C1C';
      this.glassPill = '#292929';
      this.glassOnText = '#E8E8E8';
    }
    // Autumn's skin is LIGHT, and here we have the real thing: the owner's iOS 26
    // screenshot shows a near-white warm capsule with a light GREY active pill,
    // not the peach one the provisional pass assumed. All three sampled.
    if ( p.key === 'autumn' ) {
      this.glassBg = '#FFFAF4';
      this.glassPill = '#EAE6E0';
      this.glassOnText = '#6B5344';
    }
    // Roraima's skin is DARK, and here is the device itself (screenshots
    // 2026-08-13): a warm mid-brown floating capsule with an active pill DARKER
    // than the capsule around it, not lighter — the reverse of every light skin
    // here — and muted warm labels. The provisional pass assumed a near-white
    // capsule because it assumed a sand body.
    if ( p.key === 'roraima' ) {
      // Re-sampled from a photograph of the LIVE phone (owner 2026-08-14,
      // Downloads/Screenshot 17.47.05) rather than from the design screenshots.
      // The capsule is a milky brown a good deal LIGHTER than the #958178 this
      // was set to, and the difference is not cosmetic: every tab-glyph colour
      // this theme has shipped was judged against the wrong surface, first
      // against white and then against a capsule too dark by half.
      this.glassBg = '#C0A799';
      this.glassPill = '#A88A79';
      this.glassOnText = '#5C4436';
      this.topPillBg = '#8D786D';
      this.topPillInk = '#604336';
    }
    // Miami's skin is LIGHT and its iOS-26 bar is the plain near-white one the
    // light skins share — the pink lives in the chrome above it, not in the glass
    // (screenshots 2026-08-13). Its TOP actions pill is not white though: it is a
    // lighter pink laid over the hot pink header.
    if ( p.key === 'miami' ) {
      this.glassBg = '#FFFFFF';
      this.glassPill = '#EDEDED';
      this.glassOnText = '#7E7E7E';
      this.topPillBg = '#FC88B3';
      this.topPillInk = '#544A4E';
    }
    // Sequoia's skin is LIGHT, so its glass is the near-white the light skins
    // already use — tinted to the skin's own warm off-white.
    if ( p.key === 'sequoia' ) {
      this.glassBg = '#FCFCF3';
      this.glassPill = '#E7E7DF';
      this.glassOnText = '#414540';
    }
    // Istanbul's skin is LIGHT, so it needs no dark-glass branch at all: it
    // already lands on the one-light-set rule above, and that is the right
    // answer here — the device draws a near-WHITE floating capsule. What moves
    // is only the TINT, sampled from the owner's iOS 26 screenshot (2026-08-14):
    // the capsule carries the skin's cyan cast, and the active pill is a NEUTRAL
    // grey a step DARKER than the capsule, exactly like every other light skin
    // here, with a muted slate-teal label on it.
    if ( p.key === 'istanbul' ) {
      this.glassBg = '#F7FEFF';
      this.glassPill = '#E2E9EA';
      this.glassOnText = '#4C6A70';
    }
    // Paris: PLACEHOLDER — Roraima's iOS 26 glass trio, copied verbatim. Those
    // three hexes were RE-SAMPLED today (owner 2026-08-14) from a photograph of
    // the LIVE phone showing Roraima, so they are real numbers, but they are
    // real numbers for RORAIMA's skin. The owner has not chosen Paris's LINE
    // skin yet and will set it themselves; Paris's own capsule WILL differ, and
    // every tab-glyph colour judged against this capsule has to be re-judged
    // once the real skin lands.
    // TODO(owner): resample the capsule, the active pill and the label from
    // device screenshots of the real Paris skin.
    // Paris, sampled from the device (2026-08-14): a near-WHITE capsule with a
    // faintly warm grey active pill. Worth stating plainly because Roraima's
    // turned out to be a milky BROWN and every colour chosen against the assumed
    // white was wrong — here the screenshot confirms the white rather than
    // leaving it assumed, so the tab colours can be measured on it from the start.
    if ( p.key === 'paris' ) {
      this.glassBg = '#FFF8F8';
      this.glassPill = '#EAE3E3';
      this.glassOnText = '#5A4A4A';
    }
    // Rio's skin is LIGHT and entirely neutral, so its iOS-26 bar is the plain
    // white one with a grey active pill — no tint at all to carry over from the
    // chrome (sampled 2026-08-14 21.23). This is the surface every ON glyph in
    // this theme is measured against.
    if ( p.key === 'rio-de-janeiro' ) {
      this.glassBg = '#FFFFFF';
      this.glassPill = '#EAEAEA';
      this.glassOnText = '#3C3C3C';
    }
    // Tallinn's skin is LIGHT below its azure band, so its iOS-26 bar is the plain
    // white one with a grey active pill — no tint carried down from the chrome
    // (sampled 2026-08-14 22.06). This is the surface its ON glyphs are measured
    // against.
    if ( p.key === 'tallinn' ) {
      this.glassBg = '#FFFFFF';
      this.glassPill = '#EAEAEA';
      this.glassOnText = '#3C3C3C';
    }
    // Ocean, SAMPLED (2026-08-15) — and this is the exact trap the comment here
    // used to warn about. The glass was pinned DARK on the reasoning that the
    // skin was dark; the owner's skin is light and the phone draws the near-white
    // capsule, carrying a faint mist cast rather than a neutral one. Both tab
    // glyph tones had been measured against the dark pair, so both were re-judged
    // against these (see line-tab-colors).
    if ( p.key === 'ocean' ) {
      this.glassBg = '#F4FBFB';
      this.glassPill = '#E0E7E7';
      this.glassOnText = '#102B2E';
    }
    // Salda's skin is LIGHT, so it lands on the one-light-set rule above and only
    // its TINT moves: a near-white capsule carrying the sand cast of the body,
    // with a neutral pill a step darker. DERIVED — no device has run this theme.
    // Salda, SAMPLED (2026-08-15). The derived guess was close on this one
    // surface: a near-white capsule with a warm cast and a sand pill a step down.
    // The measured values sit a hair warmer and lighter.
    if ( p.key === 'salda' ) {
      this.glassBg = '#FFFAF0';
      this.glassPill = '#EAE6DD';
      this.glassOnText = '#635555';
    }
    // Prague, SAMPLED (2026-08-15). Dark glass was the right call and the tint was
    // not: this skin is LINE's near-black, so the capsule is a neutral grey rather
    // than the navy this was derived as.
    if ( p.key === 'prague' ) {
      this.glassBg = '#1C1C1C';
      this.glassPill = '#292929';
      this.glassOnText = '#FAFAFA';
    }
    // Sydney's skin is LIGHT, so it lands on the one-light-set rule above and
    // only its tint moves: a near-white capsule carrying the harbour's cast.
    // DERIVED — no device has run this theme.
    // Sydney, SAMPLED (2026-08-15). Derived as a near-white capsule carrying the
    // harbour's cast; the phone draws a plain WHITE one with LINE's neutral grey
    // pill, no tint at all.
    if ( p.key === 'sydney' ) {
      this.glassBg = '#FFFFFF';
      this.glassPill = '#EAEAEA';
      this.glassOnText = '#3C3C3C';
    }
    // Reynisfjara's skin is DARK, so the phone draws DARK glass. DERIVED — no
    // device has run this theme.
    // Reynisfjara, SAMPLED (2026-08-16): LINE's near-black glass, the same capsule
    // Aurora and Prague draw. The derived one was a cool slate.
    if ( p.key === 'reynisfjara' ) {
      this.glassBg = '#1C1C1C';
      this.glassPill = '#292929';
      this.glassOnText = '#FAFAFA';
    }
    // Spring's skin is LIGHT, so it lands on the one-light-set rule above and only
    // its tint moves: a near-white capsule carrying the meadow's cast. DERIVED —
    // no device has run this theme.
    // Spring, SAMPLED (2026-08-16): a plain WHITE capsule with LINE's neutral grey
    // pill. The derived one carried the meadow's own tint; this skin keeps its
    // green for the search field, the input pill and the keypad and leaves every
    // other surface white.
    if ( p.key === 'spring' ) {
      this.glassBg = '#FFFFFF';
      this.glassPill = '#EAEAEA';
      this.glassOnText = '#2B2B2B';
    }
    // Vienna's skin is DARK, so the phone draws DARK glass — and warm, because
    // this skin is warm all the way through. DERIVED; no device has run it.
    // Vienna's SAMPLED skin is light, so it lands on the one-light-set rule and
    // only its tint moves: a near-white capsule carrying the blush's warmth. The
    // dark glass this replaced belonged to the wine skin.
    if ( p.key === 'vienna' ) {
      this.glassBg = '#FBF5F1';   // SAMPLED off the owner's iOS 26 shot, not derived
      this.glassPill = '#F3E2DA';
      this.glassOnText = '#A85E2C';
    }
    // Madrid's skin is LIGHT, so it lands on the one-light-set rule and only its
    // tint moves. SAMPLED from the owner's iOS 26 shot: the capsule is a pale
    // saffron rather than a near-white, because on this skin even the glass is
    // yellow.
    if ( p.key === 'madrid' ) {
      this.glassBg = '#FFE897';
      this.glassPill = '#EAD58B';
      this.glassOnText = '#3A2F08';
    }
    // Etna's SAMPLED skin is LINE's stock near-black, the same as Aurora's, so its
    // glass is the neutral dark one rather than the extra-dark warm charcoal the
    // derived version reasoned its way to.
    if ( p.key === 'etna' ) {
      this.glassBg = '#1C1C1C';
      this.glassPill = '#2C2C2C';
      this.glassOnText = '#FFFFFF';
    }
    // Fuji's skin is LIGHT, so it lands on the one-light-set rule and only its
    // tint moves: a near-white capsule with the blush's warmth in it. DERIVED.
    // SAMPLED: the capsule carries the blush rather than being near-white, because
    // on this skin even the pale surfaces are pink.
    if ( p.key === 'fuji' ) {
      this.glassBg = '#FFF0EA';
      this.glassPill = '#FBE0D9';
      this.glassOnText = '#8C5358';
    }
    // ONE source for this, shared with every menu-bg design (see lineTabFill).
    // The preview's tab bar and the bottom of the exported strip have to be the
    // same colour or the phone shows an edge under the strip; keeping two copies
    // of the value in two components is what kept letting them drift apart.
    this.tabFill = lineTabFill( p.key, p.appearance );
    this.accent = p.designTokens?.primaryColor || set.outgoing;
    // Tab tints come from the SHARED source, never a second copy — this is the
    // component whose copy had drifted and made the mockup show a glyph the
    // phone would never draw.
    this.tabOn = lineTabOn( p, 'home' );
    this.tabOff = lineTabOff( p );
  }
}
